import { X, CheckCircle2 } from "lucide-react";

interface RemoveEmployeeModalProps {
  /** Display name of the employee being removed */
  name: string;
  /** Employee status — pending invites are deleted outright, active users archived */
  status?: string;
  /** Whether the removal request is in flight */
  loading?: boolean;
  /** When true, show the success state briefly before the parent closes the modal */
  success?: boolean;
  /** Error message to surface, if the request failed */
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

export default function RemoveEmployeeModal({
  name,
  status,
  loading = false,
  success = false,
  error,
  onConfirm,
  onClose,
}: RemoveEmployeeModalProps) {
  const isPendingInvite = status === "invited";
  // While loading or showing success, the modal shouldn't be dismissable
  const locked = loading || success;

  return (
    <div
      className="crewcast-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-employee-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !locked) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape" && !locked) onClose();
      }}
      tabIndex={-1}
    >
      <div className="crewcast-modal-card" style={{ maxWidth: "480px" }}>
        {success ? (
          <div className="crewcast-modal-body remove-employee-success">
            <CheckCircle2 size={48} className="remove-employee-success-icon" />
            <p className="remove-employee-success-text">
              <strong>{name}</strong>{" "}
              {isPendingInvite ? "invite removed" : "was removed"}
            </p>
          </div>
        ) : (
          <>
            <div className="crewcast-modal-header">
              <div>
                <h2 id="remove-employee-title" className="crewcast-modal-title">
                  {isPendingInvite ? "Remove invite" : "Remove employee"}
                </h2>
              </div>
              <button
                type="button"
                className="crewcast-modal-close"
                onClick={onClose}
                disabled={loading}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="crewcast-modal-body">
              <p style={{ margin: 0, lineHeight: 1.6, color: "var(--color-gray-700)" }}>
                Are you sure you want to remove <strong>{name}</strong>?{" "}
                {isPendingInvite
                  ? "Their pending invite will be deleted so the email can be invited again."
                  : "They will be signed out, lose access immediately, and be removed from your team."}{" "}
                This action cannot be undone.
              </p>

              {error && (
                <p
                  style={{
                    marginTop: "var(--spacing-md)",
                    marginBottom: 0,
                    color: "var(--color-error)",
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  {error}
                </p>
              )}
            </div>

            <div className="crewcast-modal-actions">
              <button
                type="button"
                className="crewcast-modal-btn secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="crewcast-modal-btn danger"
                onClick={onConfirm}
                disabled={loading}
              >
                {loading ? "Removing…" : "Remove"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
